import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';

// API REQUESTS
import { getOnboardingBaseData } from 'api';

// COMPONENTS
import LoadingSpinner from '../LoadingSpinner';
import GoogleEmailDropdown from '../Onboarding_comp/GoogleEmailDropdown';
import CurrencyDropdown from '../CurrencyDropdown';
import ConfirmationModal from '../ConfirmationModal';

interface Account {
  customer_id?: string[] | string;
  login_customer_id?: string;
  properties?: string[];
  property_id?: string;
  account_id?: string;
}

interface Api {
  name: string;
  id: string;
  logo: string;
  accounts: Account[];
}

interface APIsCfgProps {
  customerId: string;
  googleEmail: string;
  setGoogleEmail: React.Dispatch<React.SetStateAction<string>>;
}

interface CustomerInfo {
  customer_id: string;
}

interface AccountConfiguration {
  google_search_ads?: Account;
  google_ads?: Account;
  google_search_console?: Account;
  google_analytics?: Account;
  microsoft_ads?: Account;
  currency_code?: string;
}

interface CustomerData {
  customer_info: CustomerInfo;
  account_configurations: AccountConfiguration[];
  google_email_account: string;
}

interface ApiResponse {
  customers: CustomerData[];
}

interface DiffRow {
  label: string;
  oldValue: string;
  newValue: string;
}

const initialAPIs: Api[] = [
  {
    name: 'Google Search Ads 360',
    id: 'google-search-ads-360',
    logo: `${process.env.PUBLIC_URL}/static/img/search_ads.png`,
    accounts: [{ customer_id: [''], login_customer_id: '' }],
  },
  {
    name: 'Google Ads',
    id: 'google-ads',
    logo: `${process.env.PUBLIC_URL}/static/img/google_ads.png`,
    accounts: [{ customer_id: [''], login_customer_id: '' }],
  },
  {
    name: 'Google Search Console',
    id: 'google-search-console',
    logo: `${process.env.PUBLIC_URL}/static/img/search_console.png`,
    accounts: [{ properties: [''] }],
  },
  {
    name: 'Google Analytics',
    id: 'google-analytics',
    logo: `${process.env.PUBLIC_URL}/static/img/analytics.png`,
    accounts: [{ property_id: '' }],
  },
  {
    name: 'Microsoft Ads',
    id: 'microsoft-ads',
    logo: `${process.env.PUBLIC_URL}/static/img/microsoft_ads.png`,
    accounts: [{ customer_id: '', account_id: '' }],
  },
];

const isGoogleEmailRequired = (apiData: Api[]) => {
  for (const { accounts } of apiData) {
    for (const account of accounts) {
      if (
        account.account_id ||
        account.property_id ||
        account.login_customer_id
      ) {
        return true;
      }

      for (const id of account.customer_id || []) {
        if (id) return true;
      }

      for (const property of account.properties || []) {
        if (property) return true;
      }
    }
  }
  return false;
};

function APIsCfg({ customerId, googleEmail, setGoogleEmail }: APIsCfgProps) {
  const queryClient = useQueryClient();
  const [apis, setApis] = useState<Api[]>(() => structuredClone(initialAPIs));
  const [originalApis, setOriginalApis] = useState<Api[]>(() =>
    structuredClone(initialAPIs)
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [changedApis, setChangedApis] = useState<Set<string>>(new Set());
  const [unlockedApis, setUnlockedApis] = useState<Set<string>>(new Set());
  const [currency, setCurrency] = useState<string>('');
  const [originalCurrency, setOriginalCurrency] = useState<string>('');
  const [currencyIsRequired, setCurrencyIsRequired] = useState<boolean>(false);
  const [isAdsAccount, setIsAdsAccount] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [diffRows, setDiffRows] = useState<DiffRow[]>([]);
  const [originalGoogleEmail, setOriginalGoogleEmail] = useState('');

  const { data: availableData } = useQuery(['base_setup_data'], {
    queryFn: () =>
      fetch('/get_available_data_base_set_up/').then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      }),
    onError: (error) => {
      console.error('Error fetching client and line of business', error);
    },
  });

  type CurrencyData = {
    country_name: string;
    currency_name: string;
    currency_code: string;
    currency_symbol: string;
  };

  const currencyList = useMemo(() => {
    if (!availableData) return [];
    const currencyListFormatted: string[] =
      availableData?.currency_data?.map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ country_name, currency_symbol, ...rest }: CurrencyData) =>
          Object.values(rest).join('-')
      ) || [];

    const uniqueCurrencyList: string[] = Array.from(
      new Set(currencyListFormatted)
    ); // [...new Set(currencyListFormatted)]; // getting error with  the spread new set
    return uniqueCurrencyList;
  }, [availableData]);

  const [isEmailDisabled, setIsEmailDisabled] = useState(false);

  const { data: onboardingData } = useQuery(['onboardingBaseData'], {
    queryFn: getOnboardingBaseData,
  });

  const googleEmailOptions = onboardingData?.google_email_accounts || [];

  const onSetGoogleEmail = (selected: string) => {
    setGoogleEmail((prev) => (selected === prev ? '' : selected));
    setHasChanges(true);
  };

  // Google Console email requirement check
  const isEmailRequired = useMemo(() => isGoogleEmailRequired(apis), [apis]);

  const { isLoading, error } = useQuery(
    ['customerInfo', customerId],
    async () => {
      const response = await fetch('/customer-info-onboard/');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data: ApiResponse = await response.json();
      return data;
    },
    {
      onSuccess: (data) => {
        const customerData = data.customers.find(
          (customer) => customer.customer_info.customer_id === customerId
        );

        if (customerData) {
          const accountConfig = customerData?.account_configurations?.[0];
          const updatedApis = initialAPIs.map((api) => {
            switch (api.id) {
              case 'google-search-ads-360':
                return accountConfig?.google_search_ads
                  ? {
                      ...api,
                      accounts: [accountConfig.google_search_ads || ''],
                    }
                  : api;
              case 'google-ads':
                return accountConfig?.google_ads
                  ? {
                      ...api,
                      accounts: [
                        {
                          ...accountConfig.google_ads,
                          // Convert customer_id to an array and remove hyphens (handles both string and string[] types)
                          customer_id: Array.isArray(
                            accountConfig.google_ads.customer_id
                          )
                            ? accountConfig.google_ads.customer_id.map(
                                (id) => id?.replace(/-/g, '') || ''
                              ) // remove hyphens
                            : [
                                accountConfig.google_ads.customer_id?.replace(
                                  /-/g,
                                  ''
                                ) || '',
                              ], // Ensure array format
                          login_customer_id:
                            accountConfig.google_ads.login_customer_id?.replace(
                              /-/g,
                              ''
                            ),
                        },
                      ],
                    }
                  : api;
              case 'google-search-console':
                return accountConfig?.google_search_console
                  ? { ...api, accounts: [accountConfig.google_search_console] }
                  : api;
              case 'google-analytics':
                return accountConfig?.google_analytics
                  ? { ...api, accounts: [accountConfig.google_analytics] }
                  : api;
              case 'microsoft-ads':
                return accountConfig?.microsoft_ads
                  ? { ...api, accounts: [accountConfig.microsoft_ads] }
                  : api;
              default:
                return api;
            }
          });
          setApis(updatedApis);
          setOriginalApis(structuredClone(updatedApis));

          if (customerData.google_email_account) {
            setIsEmailDisabled(true);
            setGoogleEmail(customerData.google_email_account);
            setOriginalGoogleEmail(customerData.google_email_account);
          }
          const currencyCode =
            customerData?.account_configurations?.[0]?.currency_code;
          if (currencyCode) {
            setCurrency(currencyCode);
            setOriginalCurrency(currencyCode);
          }
        }
      },
    }
  );

  useEffect(() => {
    // Check if google Ads or Microsoft Ads account exist
    const isGoogleAdsAccount = !!(
      apis?.[1]?.accounts?.[0].customer_id?.[0] &&
      apis?.[1]?.accounts?.[0]?.login_customer_id
    );
    const isMicrosoftAdsAccount = !!apis?.[4]?.accounts?.[0].customer_id;
    const computedIsAdsAccount = isGoogleAdsAccount || isMicrosoftAdsAccount;

    setIsAdsAccount((prev) =>
      prev !== computedIsAdsAccount ? computedIsAdsAccount : prev
    ); // check prev state to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apis, currency]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (showSuccessMessage) {
      timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    }

    return () => clearTimeout(timer);
  }, [showSuccessMessage]);

  const mutation = useMutation(
    async (updatedApis: Api[]) => {
      // Only include the changed APIs in the update payload
      const updatedData: Partial<AccountConfiguration> = {};

      changedApis.forEach((apiId) => {
        const api = updatedApis.find((api) => api.id === apiId);
        if (api && api.accounts[0]) {
          switch (api.id) {
            case 'google-search-ads-360':
              updatedData.google_search_ads = api.accounts[0];
              break;
            case 'google-ads':
              updatedData.google_ads = api.accounts[0];
              break;
            case 'google-search-console':
              updatedData.google_search_console = api.accounts[0];
              break;
            case 'google-analytics':
              updatedData.google_analytics = api.accounts[0];
              break;
            case 'microsoft-ads':
              updatedData.microsoft_ads = api.accounts[0];
              break;
            default:
              break;
          }
        }
      });

      const response = await fetch(`/customer-apicfg-update/${customerId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_configurations: updatedData,
          google_email_account: googleEmail,
          currency_code: currency,
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customerInfo', customerId]);
        queryClient.invalidateQueries(['get_customer_info_sidebar']);

        setHasChanges(false); // Reset changes after successful save
        setChangedApis(new Set()); // Clear changed APIs after successful save
      },
    }
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    apiIndex: number,
    accountIndex: number,
    itemIndex?: number
  ) => {
    const { name } = e.target;
    let value = e.target.value.trim();

    // Sanitize input of Google Ads fields - Customer ID/Login Customer ID - Remove hyphens
    if (apiIndex === 1) {
      value = value.replace(/-/g, '');
    }

    const newApis = [...apis];
    const key = name as keyof Account;

    if (
      key === 'customer_id' &&
      Array.isArray(newApis[apiIndex].accounts[accountIndex].customer_id) &&
      itemIndex !== undefined
    ) {
      const updatedCustomerIds = [
        ...(newApis[apiIndex].accounts[accountIndex].customer_id as string[]),
      ];
      updatedCustomerIds[itemIndex] = value;
      newApis[apiIndex].accounts[accountIndex].customer_id = updatedCustomerIds;
    } else if (
      key === 'properties' &&
      Array.isArray(newApis[apiIndex].accounts[accountIndex].properties) &&
      itemIndex !== undefined
    ) {
      const updatedProperties = [
        ...(newApis[apiIndex].accounts[accountIndex].properties as string[]),
      ];
      updatedProperties[itemIndex] = value;
      newApis[apiIndex].accounts[accountIndex].properties = updatedProperties;
    } else {
      (newApis[apiIndex].accounts[accountIndex][key] as string) = value;
    }

    setApis(newApis);
    setHasChanges(true); // Mark as unsaved changes
    setChangedApis((prev) => new Set(prev).add(newApis[apiIndex].id)); // Track the changed API

    // Check if either google Ads or Microsoft Ads fields are filled
    const isGoogleAdsAccount = !!(
      apis?.[1]?.accounts?.[0].customer_id?.[0] &&
      apis?.[1]?.accounts?.[0]?.login_customer_id
    );
    const isMicrosoftAdsAccount = !!apis?.[4]?.accounts?.[0].customer_id;

    setIsAdsAccount(isGoogleAdsAccount || isMicrosoftAdsAccount); // set isAdsAccount true if either Ads account is set in the form

    // For Error message if currency is not set and ads account inputted (Google Ads/Microsoft Ads)
    if (!currency && isAdsAccount) {
      setCurrencyIsRequired(true);
    } else {
      setCurrencyIsRequired(false);
    }
  };

  const fieldLabels: Partial<Record<keyof Account, string>> = {
    customer_id: 'Customer ID',
    login_customer_id: 'Login Customer ID',
    properties: 'Properties',
    property_id: 'Property ID',
    account_id: 'Account ID',
  };

  const formatDiffValue = (value: string | string[] | undefined): string => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '(empty)';
    return value ? value : '(empty)';
  };

  const accountInputClassName = (changed: boolean): string =>
    `text-gray600 text-sm border rounded p-2 flex-grow disabled:opacity-50 disabled:bg-gray100 disabled:cursor-not-allowed ${
      changed ? 'border-yellow500 bg-yellow100' : 'border-gray200'
    }`;

  // Compares a field's current value to its last-saved value, for the changed-field highlight
  const isFieldChanged = (
    apiIndex: number,
    accountIndex: number,
    key: keyof Account,
    itemIndex?: number
  ): boolean => {
    const currentAccount = apis[apiIndex]?.accounts[accountIndex];
    const originalAccount = originalApis[apiIndex]?.accounts[accountIndex];
    if (!currentAccount) return false;

    if (itemIndex !== undefined) {
      const currentArr = currentAccount[key] as string[] | undefined;
      const originalArr = originalAccount?.[key] as string[] | undefined;
      return (currentArr?.[itemIndex] || '') !== (originalArr?.[itemIndex] || '');
    }

    return (currentAccount[key] || '') !== (originalAccount?.[key] || '');
  };

  const unlockApi = (apiId: string) => {
    setUnlockedApis((prev) => new Set(prev).add(apiId));
  };

  // Cancels editing for a single API: reverts its fields to the last-saved
  // values, re-locks it, and clears it from the changed-fields tracking.
  const handleCancelEdit = (apiId: string, apiIndex: number) => {
    const revertedApis = [...apis];
    revertedApis[apiIndex] = structuredClone(originalApis[apiIndex]);
    setApis(revertedApis);

    setUnlockedApis((prev) => {
      const next = new Set(prev);
      next.delete(apiId);
      return next;
    });

    const newChangedApis = new Set(changedApis);
    newChangedApis.delete(apiId);
    setChangedApis(newChangedApis);
    setHasChanges(
      newChangedApis.size > 0 ||
        googleEmail !== originalGoogleEmail ||
        currency !== originalCurrency
    );
  };

  const buildDiffRows = (): DiffRow[] => {
    const rows: DiffRow[] = [];

    changedApis.forEach((apiId) => {
      const apiIndex = apis.findIndex((api) => api.id === apiId);
      if (apiIndex === -1) return;
      const api = apis[apiIndex];
      const originalApi = originalApis[apiIndex];

      api.accounts.forEach((account, accountIndex) => {
        const originalAccount = originalApi?.accounts[accountIndex];

        (Object.keys(fieldLabels) as (keyof Account)[]).forEach((key) => {
          if (!(key in account)) return;
          const currentValue = account[key];
          const originalValue = originalAccount?.[key];

          if (Array.isArray(currentValue)) {
            const originalArr = (originalValue as string[]) || [];
            const changed = currentValue.some(
              (v, i) => (v || '') !== (originalArr[i] || '')
            );
            if (changed) {
              rows.push({
                label: `${api.name} - ${fieldLabels[key]}`,
                oldValue: formatDiffValue(originalArr),
                newValue: formatDiffValue(currentValue),
              });
            }
          } else if ((currentValue || '') !== (originalValue || '')) {
            rows.push({
              label: `${api.name} - ${fieldLabels[key]}`,
              oldValue: formatDiffValue(originalValue as string | undefined),
              newValue: formatDiffValue(currentValue as string | undefined),
            });
          }
        });
      });
    });

    if (googleEmail !== originalGoogleEmail) {
      rows.push({
        label: 'Google Email',
        oldValue: formatDiffValue(originalGoogleEmail),
        newValue: formatDiffValue(googleEmail),
      });
    }

    if (currency !== originalCurrency) {
      rows.push({
        label: 'Currency',
        oldValue: formatDiffValue(originalCurrency),
        newValue: formatDiffValue(currency),
      });
    }

    return rows;
  };

  const handleSave = () => {
    // Validation for currency field and ads account fields
    if ((!currency && isAdsAccount) || (currency && !isAdsAccount)) {
      alert('Please correct the errors in Ads / Currency fields to proceed.');
      setCurrencyIsRequired(true);
      return;
    }

    const rows = buildDiffRows();
    if (rows.length === 0) return;

    setDiffRows(rows);
    setIsConfirmOpen(true);
  };

  const handleConfirmSave = () => {
    mutation.mutate(apis, {
      onSuccess: () => {
        setHasChanges(false);
        setChangedApis(new Set());
        setUnlockedApis(new Set());
        setOriginalApis(structuredClone(apis));
        setOriginalGoogleEmail(googleEmail);
        setOriginalCurrency(currency);
        setShowSuccessMessage(true);
        setIsConfirmOpen(false);
      },
    });
  };

  const handleAddMore = (index: number) => {
    const newApis = [...apis];
    const account = newApis[index].accounts[0];

    if ('customer_id' in account) {
      if (
        Array.isArray(account.customer_id) &&
        account.customer_id.length < 3
      ) {
        account.customer_id.push('');
      }
    } else if ('properties' in account) {
      if (Array.isArray(account.properties) && account.properties.length < 3) {
        account.properties.push('');
      }
    }

    setApis(newApis); // Mark as unsaved changes
    setHasChanges(true);
    setChangedApis((prev) => new Set(prev).add(newApis[index].id)); // Track the changed API
  };

  const handleRemove = (
    apiIndex: number,
    accountIndex: number,
    itemIndex?: number
  ) => {
    const newApis = [...apis];
    const account = newApis[apiIndex].accounts[accountIndex];

    if ('customer_id' in account && Array.isArray(account.customer_id)) {
      if (account.customer_id.length > 1 && typeof itemIndex === 'number') {
        account.customer_id.splice(itemIndex, 1);
      }
    } else if ('properties' in account && Array.isArray(account.properties)) {
      if (account.properties.length > 1 && typeof itemIndex === 'number') {
        account.properties.splice(itemIndex, 1);
      }
    }

    setApis(newApis);
    setHasChanges(true); // Mark as unsaved changes
    setChangedApis((prev) => new Set(prev).add(newApis[apiIndex].id)); // Track the changed API
  };

  const handleCurrencyChange = (option: string) => {
    setCurrency(option);
    setCurrencyIsRequired(false);
    setHasChanges(true); // Mark as unsaved changes
  };

  // Clear currency dropdown on button click
  const handleClearCurrencyChange = () => {
    setCurrency('');
    setCurrencyIsRequired(true);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>Failed to fetch data</div>;
  }

  return (
    <div className="border-y border-success py-6">
      <div className="space-y-8 pb-28">
        <GoogleEmailDropdown
          options={googleEmailOptions}
          value={googleEmail}
          onChange={onSetGoogleEmail}
          isDisabled={isEmailDisabled}
          isRequired={isEmailRequired}
        />

        {apis.map((api, index) => (
          <div key={api.id} className="space-y-2">
            <div className="flex items-center space-x-2">
              <img
                src={api.logo}
                alt={`${api.name} logo`}
                className="w-6 h-6"
              />
              <span className="text-primarygray font-bold">{api.name}</span>
              {api.id !== 'microsoft-ads' && api.id !== 'google-analytics' && (
                <div className="border border-thirdgray rounded-[20px] px-2">
                  <button
                    onClick={() => handleAddMore(index)}
                    disabled={!unlockedApis.has(api.id)}
                    className="text-sm text-thirdgray font-bold flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/static/img/plus_gray.svg`}
                      alt="plus_icon"
                      className="w-[18px] h-[18px] mr-1"
                    />
                    ADD MORE
                  </button>
                </div>
              )}
              {unlockedApis.has(api.id) ? (
                <button
                  onClick={() => handleCancelEdit(api.id, index)}
                  className="text-sm font-bold text-thirdgray border border-thirdgray rounded-[20px] px-3 py-0.5"
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={() => unlockApi(api.id)}
                  className="text-sm font-bold text-thirdgray border border-thirdgray rounded-[20px] px-3 py-0.5"
                >
                  Edit
                </button>
              )}
            </div>
            {api.accounts.map((account, accountIndex) => (
              <div
                key={accountIndex}
                className="relative flex flex-col space-y-4"
              >
                {accountIndex > 0 && (
                  <div className="absolute -top-2 right-0">
                    <button
                      onClick={() => handleRemove(index, accountIndex)}
                      disabled={!unlockedApis.has(api.id)}
                      className="text-error disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      &times;
                    </button>
                  </div>
                )}
                <div className="flex items-center space-x-6">
                  {('customer_id' in account ||
                    'login_customer_id' in account) && (
                    <>
                      <div className="flex items-center space-x-2 flex-grow">
                        <label className="text-sm font-bold">Customer ID</label>
                        <div className="flex flex-col space-y-2 flex-grow">
                          {Array.isArray(account.customer_id) ? (
                            account.customer_id.map(
                              (id: string, customerIndex: number) => (
                                <div
                                  key={customerIndex}
                                  className="flex items-center space-x-2"
                                >
                                  <input
                                    type="text"
                                    placeholder="Type here"
                                    name="customer_id"
                                    value={id}
                                    onChange={(e) =>
                                      handleChange(
                                        e,
                                        index,
                                        accountIndex,
                                        customerIndex
                                      )
                                    }
                                    disabled={!unlockedApis.has(api.id)}
                                    className={accountInputClassName(
                                      isFieldChanged(
                                        index,
                                        accountIndex,
                                        'customer_id',
                                        customerIndex
                                      )
                                    )}
                                  />
                                  {customerIndex > 0 && (
                                    <button
                                      onClick={() =>
                                        handleRemove(
                                          index,
                                          accountIndex,
                                          customerIndex
                                        )
                                      }
                                      disabled={!unlockedApis.has(api.id)}
                                      className="text-error disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      &times;
                                    </button>
                                  )}
                                </div>
                              )
                            )
                          ) : (
                            <input
                              type="text"
                              placeholder="Type here"
                              name="customer_id"
                              value={account.customer_id as string}
                              onChange={(e) =>
                                handleChange(e, index, accountIndex)
                              }
                              disabled={!unlockedApis.has(api.id)}
                              className={accountInputClassName(
                                isFieldChanged(index, accountIndex, 'customer_id')
                              )}
                            />
                          )}
                        </div>
                      </div>
                      {api.id !== 'microsoft-ads' && (
                        <div className="flex items-center space-x-2 flex-grow">
                          <label className="text-sm font-bold">
                            Login Customer ID
                          </label>
                          <input
                            type="text"
                            placeholder="Type here"
                            name="login_customer_id"
                            value={account.login_customer_id || ''}
                            onChange={(e) =>
                              handleChange(e, index, accountIndex)
                            }
                            disabled={!unlockedApis.has(api.id)}
                            className={accountInputClassName(
                              isFieldChanged(index, accountIndex, 'login_customer_id')
                            )}
                          />
                        </div>
                      )}
                    </>
                  )}
                  {'properties' in account &&
                    Array.isArray(account.properties) && (
                      <div className="flex items-center space-x-2 flex-grow">
                        <label className="text-sm font-bold">Properties</label>
                        <div className="flex flex-col space-y-2 flex-grow">
                          {account.properties.map((property, propertyIndex) => (
                            <div
                              key={propertyIndex}
                              className="flex items-center space-x-2"
                            >
                              <input
                                type="text"
                                placeholder="Type here"
                                name="properties"
                                value={property}
                                onChange={(e) =>
                                  handleChange(
                                    e,
                                    index,
                                    accountIndex,
                                    propertyIndex
                                  )
                                }
                                disabled={!unlockedApis.has(api.id)}
                                className={accountInputClassName(
                                  isFieldChanged(
                                    index,
                                    accountIndex,
                                    'properties',
                                    propertyIndex
                                  )
                                )}
                              />
                              {propertyIndex > 0 && (
                                <button
                                  onClick={() =>
                                    handleRemove(
                                      index,
                                      accountIndex,
                                      propertyIndex
                                    )
                                  }
                                  disabled={!unlockedApis.has(api.id)}
                                  className="text-error disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  {'property_id' in account && (
                    <div className="flex items-center space-x-2 flex-grow">
                      <label className="text-sm font-bold">Property ID</label>
                      <input
                        type="text"
                        placeholder="Type here"
                        name="property_id"
                        value={account.property_id || ''}
                        onChange={(e) => handleChange(e, index, accountIndex)}
                        disabled={!unlockedApis.has(api.id)}
                        className={accountInputClassName(
                          isFieldChanged(index, accountIndex, 'property_id')
                        )}
                      />
                    </div>
                  )}
                  {'account_id' in account && (
                    <div className="flex items-center space-x-2 flex-grow">
                      <label className="text-sm font-bold">Account ID</label>
                      <input
                        type="text"
                        placeholder="Type here"
                        name="account_id"
                        value={account.account_id || ''}
                        onChange={(e) => handleChange(e, index, accountIndex)}
                        disabled={!unlockedApis.has(api.id)}
                        className={accountInputClassName(
                          isFieldChanged(index, accountIndex, 'account_id')
                        )}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
        <CurrencyDropdown
          options={currencyList}
          value={currency}
          onOptionChange={handleCurrencyChange}
          isRequired={currencyIsRequired}
          isAdsAccount={isAdsAccount}
          onClear={handleClearCurrencyChange}
        />
        {/* Save Button */}
        <div className="flex justify-end mb-1">
          <button
            className={`px-4 py-2 rounded-full ${
              hasChanges ? 'bg-success' : 'bg-gray200'
            } text-light`}
            onClick={handleSave}
            disabled={!hasChanges} // Disable the button if no changes
          >
            Save
          </button>
        </div>
        {showSuccessMessage && (
          <div
            className="bg-green-100 border border-green-400 text-green-700 p-1 rounded relative w-[250px] mx-auto text-center"
            role="alert"
          >
            <span className="block sm:inline">✅ Success! Changes Saved</span>
          </div>
        )}
      </div>
      <ConfirmationModal
        isOpen={isConfirmOpen}
        header="Confirm changes"
        isLoading={mutation.isLoading}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmSave}
        content={
          <div className="flex flex-col gap-2 max-w-[420px]">
            <p>Please review the changes below before saving:</p>
            <table className="text-left">
              <thead>
                <tr className="text-thirdgray">
                  <th className="pr-4 pb-1">Field</th>
                  <th className="pr-4 pb-1">Previous</th>
                  <th className="pb-1">New</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={row.label}>
                    <td className="pr-4 py-1 font-bold align-top">
                      {row.label}
                    </td>
                    <td className="pr-4 py-1 align-top text-error">
                      {row.oldValue}
                    </td>
                    <td className="py-1 align-top text-success">
                      {row.newValue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      />
    </div>
  );
}

export default APIsCfg;
